import { useState } from 'react'
import { Button, Flex, Form, Select, Table, Tooltip, Upload } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import {
  CheckCircleOutlined,
  CloseSquareOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons'

import { useColumnLocalSearch } from 'src/libs'
import { Guard } from 'src/features/session/guard'

import { LEVEL_FILTER } from 'src/pages/worldCup2026/const'
import { filterOption } from 'src/pages/worldCup2026/utils'
import {
  usePlayerList,
  usePlayerListGate,
  useTeamOptions,
} from 'src/pages/worldCup2026/players/hooks'
import { Player } from 'src/pages/worldCup2026/players/types'
import { PlayerEditModal } from 'src/pages/worldCup2026/players/widgets'
import {
  loadPlayerListByTeam,
  updatePlayerList,
} from 'src/pages/worldCup2026/players/model'
import { showNotification } from 'src/features/notifications'

export const PlayerList = () => {
  const [playerCurrent, setPlayerCurrent] = useState<null | Player>(null)
  const [teamIdCurrent, setTeamIdCurrent] = useState<null | number>(null)
  const { loadingTeams, teamOptions } = useTeamOptions()
  const { players, loadingPlayers } = usePlayerList()
  usePlayerListGate()

  const { Item } = Form
  const idSearch = useColumnLocalSearch<Player>({
    dataIndex: 'id',
  })
  const firstNameSearch = useColumnLocalSearch<Player>({
    dataIndex: 'firstName',
  })
  const lastNameSearch = useColumnLocalSearch<Player>({
    dataIndex: 'lastName',
  })
  const fullNameSearch = useColumnLocalSearch<Player>({
    dataIndex: 'fullName',
  })

  const columns: ColumnsType<Player> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...idSearch,
    },
    {
      title: 'First name',
      dataIndex: 'firstName',
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
      ...firstNameSearch,
    },
    {
      title: 'Last name',
      dataIndex: 'lastName',
      sorter: (a, b) => a.lastName.localeCompare(b.lastName),
      ...lastNameSearch,
    },
    {
      title: 'Full name',
      dataIndex: 'fullName',
      sorter: (a, b) => a.fullName.localeCompare(b.fullName),
      ...fullNameSearch,
    },
    {
      title: 'Position type',
      dataIndex: 'positionType',
    },
    {
      title: 'Shirt Number',
      dataIndex: 'shirtNumber',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      sorter: (a, b) => Number(a.price) - Number(b.price),
      render: (price: string) => Number(price).toFixed(2),
    },
    {
      title: 'Level',
      dataIndex: 'level',
      filters: LEVEL_FILTER,
      filterMultiple: true,
      onFilter: (value, record) => record.level === Number(value),
    },
    {
      title: 'isTop',
      dataIndex: 'isTop',
      filters: [
        {
          text: 'Top Player',
          value: true,
        },
        {
          text: 'Regular Player',
          value: false,
        },
      ],
      filterMultiple: false,
      onFilter: (value, record) => record.isTop === value,
      render: (isTop: boolean) => (
        <>
          {isTop ? (
            <CheckCircleOutlined className={'text-success'} />
          ) : (
            <CloseSquareOutlined className={'text-danger'} />
          )}
        </>
      ),
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item: Player) => {
        return (
          <Guard permission="world_cup_2026_task_update">
            <Button onClick={() => setPlayerCurrent(item)}>
              <SearchOutlined style={{ fontSize: 16 }} />
            </Button>
          </Guard>
        )
      },
    },
  ]

  const getNameCurrentTeam = () =>
    teamOptions.find(({ value }) => value === teamIdCurrent)?.label

  return (
    <>
      <Flex gap={16}>
        <Item label="Team">
          <Select
            placeholder="Select a team"
            showSearch
            style={{ width: 250 }}
            loading={loadingTeams}
            options={teamOptions}
            filterOption={filterOption}
            onChange={(id) => {
              loadPlayerListByTeam({ id })
              setTeamIdCurrent(id)
            }}
          />
        </Item>
        {teamIdCurrent && (
          <Tooltip title="Uploading file will overwrite the current players parameters.">
            <Upload
              accept=".json"
              showUploadList={false}
              beforeUpload={(file) => {
                const reader = new FileReader()

                reader.onload = (e) => {
                  const json = JSON.parse(e.target?.result as string)
                  if (json.teamID !== teamIdCurrent) {
                    showNotification({
                      type: 'error',
                      message: 'Error',
                      description: `TeamID in the JSON file does not match the ${getNameCurrentTeam()} team`,
                    })
                    return
                  }
                  updatePlayerList({
                    playerList: json.players,
                    teamID: teamIdCurrent,
                  })
                }

                reader.readAsText(file)

                return false
              }}
            >
              <Button icon={<UploadOutlined />}>
                Upload player parameters for
                <strong>{getNameCurrentTeam()}</strong>team from a JSON file
              </Button>
            </Upload>
          </Tooltip>
        )}
      </Flex>
      <Table
        dataSource={players}
        bordered={true}
        className="overflow-auto"
        loading={loadingPlayers}
        rowKey="id"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: players.length,
        }}
      />
      {playerCurrent && (
        <PlayerEditModal
          item={playerCurrent}
          list={players}
          onClose={() => setPlayerCurrent(null)}
        />
      )}
    </>
  )
}
